import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import StaticAdsService from 'services/StaticAdsService';

import { ProductAsset, ProductSummary } from './types';

// Reference photos for the REAL products.
//
// The picker is the Shopify PRODUCTS sync — the same rows, ids and briefs the
// video pipeline keys its storyboards on. One searchable select rather than a
// list: the catalog is ~40 products per store and growing, and the only reason
// to be here is to work on ONE of them. Nothing renders until a product is
// chosen, and nothing is created or edited here — products come from the store
// sync, briefs from Video Creative → Product Mapping; this tab owns exactly
// the reference photos.

export default function ProductCatalog() {
  const service = useMemo(() => new StaticAdsService(), []);
  const [products, setProducts] = useState<ProductSummary[] | null>(null);
  const [selected, setSelected] = useState<ProductSummary | null>(null);
  const [assets, setAssets] = useState<ProductAsset[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInput = useRef<HTMLInputElement | null>(null);
  // dragenter/dragleave fire per element the drag crosses — count them or the
  // highlight flickers. Handlers live on window so the WHOLE page is the drop
  // target; they read the latest upload closure through a ref.
  const dragDepth = useRef(0);
  const uploadRef = useRef<(files: FileList | File[] | null) => void>(() => {});

  const loadProducts = useCallback(async () => {
    const resp = await service.listProducts();
    if (resp) setProducts(resp.products || []);
    // A failed load is an error, not an empty catalog.
    else setError('Failed to load the product catalog.');
  }, [service]);

  useEffect(() => { void loadProducts(); }, [loadProducts]);

  // Keep the selected object in sync with reloads (asset counts change on upload).
  useEffect(() => {
    if (!selected || !products) return;
    const fresh = products.find(p => p.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [products, selected]);

  const [assetsError, setAssetsError] = useState(false);
  const loadAssets = useCallback(async (productId: number) => {
    setAssets(null);
    setAssetsError(false);
    const resp = await service.listProductAssets(productId);
    if (resp) setAssets(resp.assets || []);
    // Leave assets null: "no photos yet" would be a lie, and a bare spinner
    // would never resolve — the render shows an error with Retry instead.
    else setAssetsError(true);
  }, [service]);

  useEffect(() => {
    if (selected) void loadAssets(selected.id);
    else setAssets(null);
  }, [selected?.id, loadAssets]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files: FileList | File[] | null) => {
    files = files && Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!files?.length || !selected) return;
    setUploading(true);
    setError(null);
    try {
      await service.uploadProductAssets(selected.id, files);
      await Promise.all([loadAssets(selected.id), loadProducts()]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  uploadRef.current = upload;

  useEffect(() => {
    if (!selected) return undefined;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    };
    const onOver = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current -= 1;
      if (dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); }
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      uploadRef.current(e.dataTransfer?.files || null);
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      dragDepth.current = 0;
      setDragging(false);
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeAsset = async (assetId: string) => {
    if (!selected) return;
    // Same confirm pattern as deleting a generated creative on the Output tab.
    if (!window.confirm(`Remove this photo from ${selected.title}? It will no longer be attached to new generations.`)) return;
    try {
      await service.deleteProductAsset(assetId);
      await Promise.all([loadAssets(selected.id), loadProducts()]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Removing the photo failed');
    }
  };

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Autocomplete
          size="small"
          options={products || []}
          value={selected}
          onChange={(_e, next) => setSelected(next)}
          loading={!products}
          getOptionLabel={p => p.title}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(props, p) => (
            <li {...props} key={p.id}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>{p.title}</Typography>
                <Chip
                  size="small"
                  color={p.assetCount ? 'success' : 'default'}
                  label={p.assetCount ? `${p.assetCount} photo${p.assetCount === 1 ? '' : 's'}` : 'no photos'}
                  sx={{ height: 18, fontSize: '0.6rem' }}
                />
                {p.briefText && (
                  <Chip size="small" color="info" variant="outlined" label="brief" sx={{ height: 18, fontSize: '0.6rem' }} />
                )}
              </Stack>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Product"
              placeholder="Search the Shopify catalog…"
              helperText="Synced from Shopify — the same products and briefs Video Creative uses. Briefs are edited in Video Creative → Product Mapping."
            />
          )}
          sx={{ mb: 3 }}
        />

      {error && (
        <Alert
          severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}
          action={products === null
            ? <Button color="inherit" size="small" onClick={() => { setError(null); void loadProducts(); }}>Retry</Button>
            : undefined}
        >
          {error}
        </Alert>
      )}

      {/* Nothing else renders until a product is picked — an empty grid on
          load is noise, and the picker IS the page's entry point. */}
      {selected && (
        <Box>
          {dragging && (
            // Fixed full-viewport veil; pointerEvents none so the drop passes
            // through to the window handlers.
            <Box
              sx={{
                position: 'fixed', inset: 12, zIndex: theme => theme.zIndex.modal + 1, pointerEvents: 'none',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 1, borderRadius: 2, border: '4px dashed', borderColor: 'primary.main',
                bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(20, 24, 46, 0.82)' : 'rgba(255, 255, 255, 0.82)',
              }}
            >
              <AddPhotoAlternateOutlinedIcon color="primary" sx={{ fontSize: 56 }} />
              <Typography variant="h6" color="primary">
                Drop to upload to {selected.title}
              </Typography>
            </Box>
          )}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap>{selected.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selected.handle}{selected.vendor ? ` · ${selected.vendor}` : ''}
              </Typography>
            </Box>
            <input
              ref={fileInput}
              type="file" accept="image/*" multiple hidden
              onChange={e => upload(e.target.files)}
            />
            <Button
              size="small" variant="contained" startIcon={<AddPhotoAlternateOutlinedIcon />}
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
            >
              {uploading ? 'Uploading…' : 'Upload photos'}
            </Button>
          </Stack>

          {!assets ? (
            assetsError ? (
              <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={() => { void loadAssets(selected.id); }}>Retry</Button>}
              >
                Failed to load this product&apos;s photos.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            )
          ) : !assets.length ? (
            <Typography variant="body2" color="text.disabled">No photos yet — drop images anywhere here, or use Upload photos.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5 }}>
              {assets.map(asset => (
                <Card key={asset.assetId} variant="outlined">
                  <Box sx={{ position: 'relative', aspectRatio: '1 / 1', bgcolor: 'grey.100' }}>
                    <Box
                      component="img"
                      src={asset.thumbnailUrl || asset.imageUrl || ''}
                      alt={asset.caption || 'Product reference'}
                      loading="lazy"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    {asset.higgsfieldMediaId && (
                      <Tooltip title="Has been sent to Higgsfield as a reference">
                        <Chip
                          size="small" color="success" label="used"
                          sx={{ position: 'absolute', top: 4, right: 4, height: 18, fontSize: '0.6rem' }}
                        />
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => removeAsset(asset.assetId)}
                      sx={{ position: 'absolute', bottom: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ p: 0.75 }}>
                    <Typography variant="caption" noWrap title={asset.caption || ''}>
                      {asset.caption || '—'}
                    </Typography>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
